import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';

import { useResizeObserver } from 'usehooks-ts';

import { Modal, Table, Row, Col, Input, Divider, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import Sites from '@flc/components/cities/sites';

import { exception, securityRestriction } from '@extensions/notification';
import { delayAction } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { serverFetch } from '@flc/server';

import { IUserSession } from '@entities/user-session';
import { ICity } from '@entities/city';
import { ICityFilter } from '@entities/city-filter';

import { Permission, hasPermission } from '@enums/permission';

const filterContext: string = 'Cities';

const Cities = () => {
    const initFilter: ICityFilter = {};

    const navigate = useNavigate();
    const resolved = useResolvedPath('');

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const [api, contextHolder] = notification.useNotification();
    const [modal, modalContextHolder] = Modal.useModal();

    const filter = useAppSelector<ICityFilter>((s) => s.filters[filterContext]);

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [cities, setCities] = useState<Array<ICity>>([]);
    const [currentCity, setCurrentCity] = useState<ICity>();

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [showFilter, setShowFilter] = useState<boolean>(true);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);

        setCities([]);

        setLoading(true);

        serverFetch('cities', { method: 'GET', queryParams: filter })
            .then((data) => {
                setCities(data);
                setLoading(false);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        setCurrentCity(undefined);
        setSelectedIds([]);

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onCityDelete = () => {
        if (!currentCity) return;

        serverFetch(`cities/${currentCity.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка удаления города', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 300 }}
                            key='searchText'
                            placeholder='Название'
                            value={filter?.searchText}
                            onChange={(data) => {
                                d(setFilter({ ...filter, searchText: data.target.value }, filterContext));
                            }}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        type: 'primary',
                        label: 'Добавить',
                        key: 'add',
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageCities)) {
                                securityRestriction(api, [Permission.ManageCities]);
                                return;
                            }

                            navigate(`${resolved.pathname}/new`);
                        },
                    },
                    {
                        label: `${hasPermission(userSession.permissions, Permission.ManageCities) ? 'Изменить' : 'Информация о городе'}`,
                        key: 'edit',
                        disabled: !currentCity,
                        icon: <EditOutlined />,
                        onClick: () => {
                            navigate(`${resolved.pathname}/${currentCity?.id}`);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: !currentCity,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteCity)) {
                                securityRestriction(api, [Permission.DeleteCity]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить город "${currentCity?.name}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onCityDelete();
                                },
                            });
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = cities.find((o) => o.id == selectedRowKeys[0]);
            setCurrentCity(entity);
        } else {
            setCurrentCity(undefined);
        }
    };

    const renderTable = () => {
        const columns: ColumnsType<ICity> = [
            {
                title: 'Название',
                dataIndex: 'name',
                width: 200,
            },
            {
                title: 'Расстояние от Москвы / км',
                dataIndex: 'distance',
                align: 'center',
                width: 200,
            },
            {},
        ];

        const tableLoading = {
            spinning: loading,
            indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
        };

        return (
            <Table
                rowKey='id'
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'radio',
                }}
                columns={columns}
                dataSource={cities}
                loading={tableLoading}
                onRow={(record: ICity) => {
                    return {
                        onClick: (event) => {
                            if (!record.id) return;

                            onSelectChange([record.id]);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: height ?? 0 }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 192px - ${filterHeight}px)` }}>
            <Row>
                <Col span={9}>
                    {renderToolbar()}
                    {renderFilter()}
                    {renderTable()}
                </Col>
                <Col offset={1} span={14}>
                    <Divider titlePlacement='left'>Адреса разгрузки</Divider>
                    <Sites api={api} cityId={currentCity?.id} />
                </Col>
            </Row>

            {contextHolder}
            {modalContextHolder}
        </div>
    );
};

export default Cities;
